<template>
  <div :id="`${source}_verify_phone_number`" class="text-normal proxima_nova">
    <CustomModal
      title="Complete verification to purchase US numbers"
      :visible="visible"
      @update:visible="updateVisible"
      width="32vw"
    >
      <div class="flex flex-col gap-6">
        <span class="text-sm"
          >Verify your workspace in to comply with local regulations for getting
          phone numbers in the United States.</span
        >

        <div
          v-if="!verificationInProgress"
          id="phone_number"
          class="flex flex-col gap-2"
        >
          <span class="text-[13px] secondary_color"
            >Phone number to receive OTP</span
          >
          <vue-tel-input
            v-model="phoneNumber"
            :defaultCountry="defaultCountryCode"
            :dropdownOptions="{
              showFlags: true,
              showDialCodeInList: true,
              showSearchBox: true,
              searchBoxPlaceholder: 'Search country..',
            }"
            mode="international"
            @keyup.enter="toSendCode"
          />
        </div>

        <div
          v-else
          id="otp_section"
          class="flex flex-col gap-6 w-full items-center justify-center"
        >
          <div class="flex flex-col gap-2 items-center justify-center">
            <span class="text-[13px] secondary_color"
              >Enter code sent to <b>{{ phoneNumber }}</b></span
            >
            <OTPFields
              source="verification"
              v-model:value="otpValue"
              :length="6"
              :error="showError"
              :errorMessage="errorMessage"
              @update:enter="toVerifyCode"
            />
          </div>

          <div
            class="flex flex-row gap-[6px] items-center text-[13px] font-normal"
          >
            <div class="cursor-pointer" @click="changeNumber">
              Change Number
            </div>
            ∙
            <div
              @click="resendCode"
              :class="
                resendDisabled
                  ? 'opacity-40 cursor-not-allowed'
                  : 'cursor-pointer transition duration-300'
              "
            >
              Resend code
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <a-button
          v-if="!verificationInProgress"
          class="primary_medium"
          :loading="loading"
          :disabled="sendCodeDisabled"
          @click="toSendCode"
          >{{ loading ? "Sending.." : "Send Code" }}</a-button
        >

        <a-button
          v-else
          class="primary_medium"
          :loading="loading"
          :disabled="verifyDisabled"
          @click="toVerifyCode"
        >
          {{ loading ? "Verifying.." : "Verify" }}</a-button
        >
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from "vue";
import { message } from "ant-design-vue";
import { VueTelInput } from "vue-tel-input";
import { isValidNumber } from "@/src/utils/callsUtils.js";
import {
  numberVerificationErrors,
  numberVerificationErrorStatement,
} from "@/src/constants/phoneNumberConstants.js";
import store from "@/src/store/index.js";
import CallService from "@/src/api/CallService.js";
import CustomModal from "@/src/components/CustomModal.vue";
import OTPFields from "@/src/components/security_components/OTPFields.vue";

const defaultCountryCode = computed(() => store.getters["workspace/defaultCountryCode"]);

onMounted(() => {
  store.dispatch("workspace/fetchDefaultCountryCode");
});

const props = defineProps({
  source: { type: String, required: true },
  visible: Boolean,
});

const emit = defineEmits(["update:visible"]);

const loading = ref(false);
const otpValue = ref("");
const phoneNumber = ref("");
const verificationInProgress = ref(false);
const errorMessage = ref("");
const invalidOtpRetries = ref(0);
const expiryTimer = ref(null);
const otpExpired = ref(false);
const resendTimer = ref(null);
const resendCountdown = ref(0);

const showError = computed(() => {
  return (
    invalidOtpRetries.value >= 5 || otpExpired.value || !!errorMessage.value
  );
});

const sendCodeDisabled = computed(() => {
  return !phoneNumber.value;
});

const verifyDisabled = computed(() => {
  return (
    otpValue.value.length !== 6 ||
    invalidOtpRetries.value >= 5 ||
    otpExpired.value
  );
});

const resendDisabled = computed(() => {
  return resendCountdown.value > 0;
});

const resetValues = () => {
  try {
    verificationInProgress.value = false;
    otpValue.value = "";
    phoneNumber.value = "";
    errorMessage.value = "";
    invalidOtpRetries.value = 0;

    if (expiryTimer.value) {
      clearTimeout(expiryTimer.value);
      expiryTimer.value = null;
    }

    if (resendTimer.value) {
      clearInterval(resendTimer.value);
      resendTimer.value = null;
    }
    resendCountdown.value = 0;
    otpExpired.value = false;
  } catch (error) {
    console.error("An error occurred while resetting values: ", error);
  }
};

const updateVisible = (value) => {
  try {
    emit("update:visible", value);
    if (!value) {
      resetValues();
    }
  } catch (error) {
    console.error("An error occurred while updating visible: ", error);
  }
};

const resetErrors = () => {
  try {
    errorMessage.value = "";
    invalidOtpRetries.value = 0;
    otpExpired.value = false;
    if (expiryTimer.value) {
      clearTimeout(expiryTimer.value);
      expiryTimer.value = null;
    }
  } catch (error) {
    console.error("An error occurred while resetting error: ", error);
  }
};

const changeNumber = () => {
  try {
    verificationInProgress.value = false;
    otpValue.value = "";
    resetErrors();
    if (resendTimer.value) {
      clearInterval(resendTimer.value);
      resendTimer.value = null;
    }
    resendCountdown.value = 0;
  } catch (error) {
    console.error("An error occurred while verifying number: ", error);
  }
};

const startResendTimer = () => {
  resendCountdown.value = 30;

  if (resendTimer.value) {
    clearInterval(resendTimer.value);
  }

  resendTimer.value = setInterval(() => {
    if (resendCountdown.value > 0) {
      resendCountdown.value -= 1;
    } else {
      clearInterval(resendTimer.value);
      resendTimer.value = null;
    }
  }, 1000);
};

const resendCode = () => {
  try {
    if (resendDisabled.value) return;

    resetErrors();
    toSendCode();
  } catch (error) {
    console.error("An error occurred while resending code: ", error);
  }
};

const startExpiryTimer = () => {
  if (expiryTimer.value) {
    clearTimeout(expiryTimer.value);
  }

  expiryTimer.value = setTimeout(
    () => {
      otpExpired.value = true;
      errorMessage.value =
        numberVerificationErrorStatement[numberVerificationErrors.EXPIRED];
    },
    5 * 60 * 1000,
  );
};

const toSendCode = async () => {
  try {
    if (!isValidNumber(phoneNumber.value)) {
      message.error("Please enter valid phone number");
      return;
    }

    loading.value = true;

    const payload = {
      phone_number: phoneNumber.value,
    };

    const response = await CallService.sendCode(payload);
    if (response?.success) {
      verificationInProgress.value = true;
      message.success(`Code sent to ${phoneNumber.value} successfully!`);
      startExpiryTimer();
      startResendTimer();
    }
  } catch (error) {
    message.error(
      error?.response?.data?.error || "An error occurred while sending code",
    );
    console.error("An error occurred while sending code: ", error);
  } finally {
    loading.value = false;
  }
};

const toVerifyCode = async () => {
  try {
    if (!otpValue.value || otpValue.value.length !== 6) {
      message.error("Please enter a valid 6-digit OTP!");
      return;
    }

    if (invalidOtpRetries.value >= 5) {
      errorMessage.value =
        numberVerificationErrorStatement[numberVerificationErrors.MAX_RETRIES];
      return;
    }

    if (otpExpired.value) {
      errorMessage.value =
        numberVerificationErrorStatement[numberVerificationErrors.EXPIRED];
      return;
    }

    loading.value = true;

    const payload = {
      otp: otpValue.value,
      phone_number: phoneNumber.value,
    };

    const response = await CallService.verifyCode(payload);
    if (response?.success) {
      message.success("Verification successful!");
      await store.dispatch("workspace/setIsPhoneNumberVerified", true);
      updateVisible(false);
    }
  } catch (error) {
    const err = error?.response?.data?.error;

    if (
      err === numberVerificationErrorStatement[numberVerificationErrors.INVALID]
    ) {
      invalidOtpRetries.value += 1;
      errorMessage.value =
        numberVerificationErrorStatement[numberVerificationErrors.INVALID];

      if (invalidOtpRetries.value >= 5) {
        errorMessage.value =
          numberVerificationErrorStatement[
            numberVerificationErrors.MAX_RETRIES
          ];
      }
    } else {
      errorMessage.value = err || "An error occurred while verifying code!";
    }

    console.error("An error occurred while verifying code: ", error);
  } finally {
    loading.value = false;
  }
};
</script>
